import React, { useEffect, useState } from "react";
import Styles from "./Register.module.css";
import SelectInput from "../../../commons/Inputs/Select/SelectInput";
import PersonIcon from "@mui/icons-material/Person";
import { sentOTP, verifyOTP } from "../../../Services/ApiCalling/Auth";
import OtpInput from "react-otp-input";
import ResendTimer from "./OTP/ResendTimer";
import { registerPageList } from "../../../commons/FixedValue";
import MainData from "./Elements/MainData";
import { ReactComponent as GnaLogo } from "../../../assets/images/GNA_Logo.svg";
import { ReactComponent as Edit } from "../../../assets/images/Edit.svg";
import StepperList from "./Stepper";
import { useSelector } from "react-redux";
import handleNav from "../../../helpers/handleActiveRoleNav";
import { toast } from "react-toastify";
import { toastProperties } from "../../../commons/Toast/Toaster";

const userTypes = [
  {
    id: 1,
    name: "Consumer",
    case: "consumer",
    value: ["COMPANY_ADMIN", "CONSUMER"],
  },
  {
    id: 2,
    name: "Generator",
    case: "generator",
    value: ["COMPANY_ADMIN", "GENERATOR"],
  },
  {
    id: 3,
    name: "Prosumer",
    case: "prosumer",
    value: ["COMPANY_ADMIN", "GENERATOR", "CONSUMER"],
  },
  {
    id: 4,
    name: "Discom",
    case: "discom",
    value: ["COMPANY_ADMIN", "GENERATOR", "CONSUMER", "BANKING"],
  },
  {
    id: 5,
    name: "Trader",
    case: "trader",
    value: ["COMPANY_ADMIN", "TRADER", "TRADER_ENTITY", "ADMIN_TRADER"],
  },
];

const RegisterComponent = ({
  disableNavigation = false,
  onComplete = null,
  isOnboardingMode = false,
}) => {
  const currUser = JSON.parse(sessionStorage.getItem("currUser"));
  const currEmail = sessionStorage.getItem("currEmail");
  const currentView = sessionStorage.getItem("currentView");
  const [viewType, setViewType] = useState(currentView || "selectuser");
  const [user, setUser] = useState(currUser || {});
  const [email, setEmail] = useState(currEmail || "");
  const [otp, setOtp] = useState("");
  const [resend, setResend] = useState(false);
  const [otpSentTime, setOtpSentTime] = useState(null);
  const [isOtpExpired, setIsOtpExpired] = useState(false);

  const handleUser = (data) => setUser(data);
  const handleEmail = (data) => setEmail(data);
  const handleResendActive = () => setResend(true);
  const [loadingOtp, setLoadingOtp] = useState(false);
  const [loadingEmail, setLoadingEmail] = useState(false);

  const token = useSelector((state) => state?.persistedReducer?.token);
  const activeRole = useSelector(
    (state) => state?.persistedReducer?.user?.activeRole,
  );

  useEffect(() => {
    setViewType(currentView || "selectuser");
  }, [currentView]);

  useEffect(() => {
    if (token && !disableNavigation && !isOnboardingMode) {
      handleNav(activeRole);
    } else if (token && disableNavigation && onComplete && !isOnboardingMode) {
      onComplete(activeRole);
    }
  }, [token, activeRole, disableNavigation, onComplete, isOnboardingMode]);

  const handleOTP = async () => {
    setLoadingOtp(true);
    if (!otp) {
      setLoadingOtp(false);
      toast.error("Please enter the OTP", toastProperties);
      return;
    }
    try {
      const userOtp = await verifyOTP(email, otp);
      let data = userOtp?.data;
      if (data?.code === 200 || data?.success === true) {
        sessionStorage.setItem("currentView", "user_details");
        sessionStorage.setItem("currEmail", email);
        sessionStorage.setItem("currUser", JSON.stringify(user));
        setViewType("user_details");
        setIsOtpExpired(false);
      } else {
        const errorMessage = data?.message || "OTP verification failed";

        if (
          errorMessage.toLowerCase().includes("expired") ||
          errorMessage.toLowerCase().includes("expire")
        ) {
          setIsOtpExpired(true);
          setResend(true);
          setOtp("");
        }

        toast.error(errorMessage, toastProperties);
      }
    } catch (err) {
      toast.error("Error verifying OTP", toastProperties);
    } finally {
      setLoadingOtp(false);
    }
  };

  const handleSendOTP = async () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      toast.error("Email is required", toastProperties);
      return;
    }
    setLoadingEmail(true);
    try {
      const res = await sentOTP(trimmedEmail);

      let data = res?.data;

      if (
        (data?.code === 200 || data?.success === true) &&
        data?.success !== false
      ) {
        setViewType("otp");
        setOtp("");
        setResend(false);
        setOtpSentTime(Date.now());
        setIsOtpExpired(false);
      } else {
        const errorMessage = data?.message || "Failed to send OTP";

        toast.error(errorMessage, toastProperties);
      }
    } catch (err) {
      let errorData = null;
      if (err?.response?.data) {
        errorData = err.response.data;
      } else if (err?.data) {
        errorData = err.data;
      }

      const errorMessage = errorData?.message || "Error sending OTP";
      toast.error(errorMessage, toastProperties);
    } finally {
      setLoadingEmail(false);
    }
  };

  const handleResendOTP = async () => {
    setOtp("");
    setResend(false);
    setLoadingEmail(true);
    setIsOtpExpired(false);
    try {
      const res = await sentOTP(email.trim());
      let data = res?.data;
      if (data?.code === 200 || data?.success === true) {
        toast.success("OTP sent successfully", toastProperties);
        setOtpSentTime(Date.now());
      } else {
        const errorMessage = data?.message || "Failed to send OTP";
        toast.error(errorMessage, toastProperties);
      }
    } catch (err) {
      toast.error("Error sending OTP", toastProperties);
    } finally {
      setLoadingEmail(false);
    }
  };

  const handleViewType = (data) => {
    sessionStorage.setItem("currentView", data);
    setViewType(data);
  };

  const renderSelectUserView = () => (
    <div className={Styles.inputMainContainer}>
      <SelectInput
        onChange={handleUser}
        list={userTypes}
        value={user}
        example={"Select Entity Type"}
        heading={"Register as"}
        height={"45px"}
      />
      <div className={Styles.registerInputCont}>
        <div className={Styles.registerInputElemCOnt}>Email ID</div>
        <div className={Styles.inputFieldCont}>
          <PersonIcon style={{ fontSize: "16px" }} />
          <div className={Styles.inputField}>
            <input
              type="text"
              placeholder="Eg. gnaenergy"
              value={email}
              onChange={(e) => handleEmail(e.target.value)}
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderOtpView = () => (
    <div className={Styles.inputMainContainer}>
      <div className={Styles.regUserDetails}>
        <div className={Styles.regUserText}>
          <b>Email Id:</b> <span>{email}</span>
        </div>
        <div className={Styles.regUserText}>
          <b>Registered as:</b> <span>{user?.name}</span>
        </div>
      </div>
      <div className={Styles.otpInputElem}>
        <OtpInput
          inputType="text"
          value={otp}
          onChange={setOtp}
          numInputs={6}
          renderSeparator={<span></span>}
          renderInput={(props) => <input {...props} />}
        />
      </div>
      <div className={Styles.otpInputElemResend}>
        {resend || isOtpExpired ? (
          <b
            onClick={loadingEmail ? undefined : handleResendOTP}
            style={{
              cursor: loadingEmail ? "not-allowed" : "pointer",
              opacity: loadingEmail ? 0.6 : 1,
            }}
          >
            {loadingEmail ? "Sending..." : "Resend OTP"}
          </b>
        ) : (
          <span>
            <ResendTimer
              key={otpSentTime || "initial"}
              handleResendActive={handleResendActive}
            />
          </span>
        )}
      </div>
    </div>
  );

  const renderCommonDetailsView = () => (
    <div className={Styles.commonDetailsSections}>
      <div className={Styles.sectionNavbar}>
        <div onClick={() => !disableNavigation && (window.location = "/")}>
          <GnaLogo style={{ fontSize: "24px" }} />
        </div>
        {/* {!isOnboardingMode && (
          <div
            className={Styles.lcc_cont}
            onClick={() =>
              !disableNavigation &&
              (window.location = "/landing_cost_calculator")
            }
          >
            Landing Cost Calculator
          </div>
        )} */}
      </div>
      <div className={Styles.inputMainCont}>
        <div className={Styles.inputCommonElem}>
          <div className={Styles.otcHeading}>
            Welcome to the <b>GNA Energy</b> OTC Platform
          </div>
          <div className={Styles.containElem}>
            <StepperList
              list={registerPageList[user?.case]}
              current={viewType}
              handleViewType={setViewType}
            />
          </div>
          <div className={Styles.emailConsumerCont}>
            <div className={Styles.displayElemContainer}>
              <div className={Styles.elementContainer}>
                <span className={Styles.elemHeading}>Email Id:</span>
                <span>{email}</span>
              </div>
              <Edit
                style={{
                  cursor: "pointer",
                  fontSize: "16px",
                  color: "var(--primary-color-text)",
                }}
                onClick={() => setViewType("selectuser")}
              />
            </div>
            <div className={Styles.displayElemContainer}>
              <div className={Styles.elementContainer}>
                <span className={Styles.elemHeading}>Registered as: </span>
                <span>{user?.name}</span>
              </div>
              <Edit
                style={{
                  cursor: "pointer",
                  fontSize: "16px",
                  color: "var(--primary-color-text)",
                }}
                onClick={() => setViewType("selectuser")}
              />
            </div>
          </div>
        </div>
        <div className={Styles.inputElemSection}>
          <div className={Styles.inputMainElemContainer}>
            <MainData
              email={email}
              user={user}
              viewType={viewType}
              handleViewType={handleViewType}
              handleClose={onComplete}
              isOnboardingMode={isOnboardingMode}
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderModalContent = () => (
    <div className={Styles.popupContainer}>
      <div className={Styles.registerCompDisplayNameCont}>
        <div className={Styles.compNameText}>GNA Energy</div>
        <div className={Styles.platformText}>OTC Platform</div>
      </div>
      <div className={Styles.registerHeadingCont}>
        <div className={Styles.registerHeadingText}>
          {viewType === "otp" ? "OTP" : "Register"}
        </div>
        <div className={Styles.textView}>
          {viewType === "otp" ? <span>OTP sent to your Email ID</span> : ""}
        </div>
      </div>
      {viewType === "selectuser" ? renderSelectUserView() : renderOtpView()}
      <button
        disabled={loadingOtp || loadingEmail}
        className={Styles.modalButton}
        onClick={() => (viewType === "otp" ? handleOTP() : handleSendOTP())}
      >
        {loadingOtp || loadingEmail ? (
          "Please wait..."
        ) : (
          <> {viewType === "otp" ? "Continue" : "Submit"}</>
        )}
      </button>
      {viewType === "otp" ? (
        <button
          type="button"
          onClick={() => {
            setViewType("selectuser");
            setOtp("");
            setResend(false);
            setOtpSentTime(null);
          }}
          className={Styles.backBtn}
        >
          Back
        </button>
      ) : (
        <>
          {!isOnboardingMode && (
            <div className={Styles.alreadyMemberText}>
              <span>Already a Member?</span>
              <span
                className={Styles.loginBtn}
                onClick={() =>
                  !disableNavigation && (window.location = "/login")
                }
              >
                Login
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );

  return (
    <>
      {viewType === "selectuser" || viewType === "otp" ? (
        <div className={Styles.type1MainContainer}>
          {/* {!isOnboardingMode && (
            <div
              className={Styles.lccButton}
              onClick={() =>
                !disableNavigation &&
                (window.location = "/landing_cost_calculator")
              }
            >
              Landing Cost Calculator
            </div>
          )} */}
          <div className={Styles.popupViewMainContainer}>
            {renderModalContent()}
          </div>
          <div className={Styles.bottomText}>
            © {new Date().getFullYear()}, <b>GNA Energy</b> Private Limited All
            Rights Reserved
          </div>
        </div>
      ) : (
        renderCommonDetailsView()
      )}
    </>
  );
};

export default RegisterComponent;
